import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { StarField, AuraLogo } from '../components/Brand';
import { useUserStore, roleHome } from '../store/userStore';
import { createUser, getConsentDefinitions, saveConsent } from '../lib/api';

const CONCERNS = [
  'Hot flushes', 'Night sweats', 'Sleep issues', 'Brain fog', 'Anxiety', 'Low mood',
  'Weight gain', 'Fatigue', 'Joint pain', 'Irregular periods', 'Low libido', 'Memory issues',
];

const STAGES = [
  { key: 'pre', label: 'Pre-menopause', desc: 'Regular cycles, early changes' },
  { key: 'peri', label: 'Perimenopause', desc: 'Cycles changing, symptoms emerging' },
  { key: 'post', label: 'Post-menopause', desc: '12+ months without a period' },
];

export default function Onboarding() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const setUser = useUserStore((s) => s.setUser);

  const [step, setStep] = useState(1);
  const [name, setName] = useState('');
  const [email, setEmail] = useState(params.get('email') || '');
  const [age, setAge] = useState('');
  const [stage, setStage] = useState('');
  const [concerns, setConcerns] = useState([]);
  const [saving, setSaving] = useState(false);
  const [consentDefs, setConsentDefs] = useState([]);
  const [consents, setConsents] = useState({});

  useEffect(() => {
    getConsentDefinitions()
      .then((defs) => {
        setConsentDefs(defs || []);
        const init = {};
        (defs || []).forEach((d) => { init[d.type] = !!d.required; });
        setConsents(init);
      })
      .catch(() => {});
  }, []);

  const toggleConcern = (c) =>
    setConcerns((prev) => (prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]));

  const next = () => {
    if (step === 1) {
      if (!name.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) || !age) {
        toast.error('Please complete your name, a valid email and age');
        return;
      }
    }
    if (step === 2 && !stage) {
      toast.error('Please select your life stage');
      return;
    }
    setStep((s) => s + 1);
  };

  const finish = async () => {
    if (!consents.health_data_processing) {
      toast.error('We need consent to process your health data to provide care.');
      return;
    }
    setSaving(true);
    try {
      const user = await createUser({
        name: name.trim(),
        email: email.trim().toLowerCase(),
        age: parseInt(age, 10),
        menopause_stage: stage,
        primary_concerns: concerns,
        lifestyle_factors: {},
        role: 'patient',
      });
      // persist consent choices
      await Promise.all(
        consentDefs.map((d) => saveConsent(user.id, d.type, !!consents[d.type]).catch(() => {}))
      );
      const finalUser = { ...user, onboarding_completed: true };
      setUser(finalUser);
      toast.success('Welcome to Auralis');
      navigate(roleHome('patient'), { replace: true });
    } catch (e) {
      toast.error('Could not create your account. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen aura-space relative flex items-center justify-center px-4 py-10">
      <StarField count={70} />
      <div className="relative w-full max-w-lg animate-fade-up">
        <div className="flex items-center gap-3 mb-6">
          <AuraLogo size={44} />
          <div className="flex items-baseline gap-2">
            <span className="font-display text-2xl text-white font-bold">Auralis</span>
            <span className="font-display text-2xl text-white/70 font-light">Health</span>
          </div>
        </div>

        {/* progress */}
        <div className="flex gap-2 mb-6">
          {[1, 2, 3, 4].map((s) => (
            <div
              key={s}
              className="h-1.5 flex-1 rounded-full transition-base"
              style={{ background: s <= step ? 'linear-gradient(90deg,#E879F9,#7C3AED)' : 'rgba(255,255,255,0.12)' }}
            />
          ))}
        </div>

        <div className="glass-dark rounded-3xl p-6 sm:p-8">
          {step === 1 && (
            <div>
              <h2 className="font-display text-2xl text-white mb-1">Let&apos;s get to know you</h2>
              <p className="text-white/60 text-sm mb-6">A few details to personalise your care.</p>
              <label className="block text-white/70 text-xs mb-1">Full name</label>
              <input
                data-testid="onboarding-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full mb-4 rounded-xl bg-white/5 border border-white/10 px-4 py-3 text-white outline-none focus:border-[var(--aura-purple-light)]"
                placeholder="Jane Doe"
              />
              <label className="block text-white/70 text-xs mb-1">Email</label>
              <input
                data-testid="onboarding-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full mb-4 rounded-xl bg-white/5 border border-white/10 px-4 py-3 text-white outline-none focus:border-[var(--aura-purple-light)]"
                placeholder="you@email.com"
              />
              <label className="block text-white/70 text-xs mb-1">Age</label>
              <input
                data-testid="onboarding-age"
                type="number"
                value={age}
                onChange={(e) => setAge(e.target.value)}
                className="w-full rounded-xl bg-white/5 border border-white/10 px-4 py-3 text-white outline-none focus:border-[var(--aura-purple-light)]"
                placeholder="48"
              />
            </div>
          )}

          {step === 2 && (
            <div>
              <h2 className="font-display text-2xl text-white mb-1">Your life stage</h2>
              <p className="text-white/60 text-sm mb-6">This helps us interpret your patterns.</p>
              <div className="flex flex-col gap-3">
                {STAGES.map((s) => (
                  <button
                    key={s.key}
                    data-testid={`onboarding-stage-${s.key}`}
                    onClick={() => setStage(s.key)}
                    className={`text-left rounded-2xl px-4 py-4 border transition-base ${
                      stage === s.key
                        ? 'border-[var(--aura-purple-light)] bg-[rgba(168,85,247,0.15)]'
                        : 'border-white/10 bg-white/5 hover:bg-white/10'
                    }`}
                  >
                    <div className="text-white font-semibold">{s.label}</div>
                    <div className="text-white/55 text-sm">{s.desc}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === 3 && (
            <div>
              <h2 className="font-display text-2xl text-white mb-1">What brings you here?</h2>
              <p className="text-white/60 text-sm mb-6">Select any concerns that resonate (optional).</p>
              <div className="flex flex-wrap gap-2">
                {CONCERNS.map((c) => (
                  <button
                    key={c}
                    data-testid={`onboarding-concern-${c.replace(/\s+/g, '-').toLowerCase()}`}
                    onClick={() => toggleConcern(c)}
                    className={`rounded-full px-4 py-2 text-sm border transition-base ${
                      concerns.includes(c)
                        ? 'border-[var(--aura-purple-light)] bg-[rgba(168,85,247,0.2)] text-white'
                        : 'border-white/10 bg-white/5 text-white/70 hover:bg-white/10'
                    }`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === 4 && (
            <div>
              <h2 className="font-display text-2xl text-white mb-1">Your privacy choices</h2>
              <p className="text-white/60 text-sm mb-5">You're in control of your data. Review these before you start — you can change them anytime in your profile.</p>
              <div className="space-y-3">
                {consentDefs.map((d) => {
                  const on = !!consents[d.type];
                  return (
                    <div key={d.type} className="flex items-start gap-3 rounded-2xl bg-white/5 border border-white/10 p-3" data-testid={`onboarding-consent-${d.type}`}>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-white font-medium text-sm">{d.title}</span>
                          {d.required && <span className="text-[10px] uppercase tracking-wide rounded-full px-2 py-0.5 bg-[rgba(168,85,247,0.25)] text-white/90">Required</span>}
                        </div>
                        <p className="text-white/55 text-xs mt-0.5">{d.description}</p>
                      </div>
                      <button
                        data-testid={`onboarding-consent-toggle-${d.type}`}
                        onClick={() => setConsents((c) => ({ ...c, [d.type]: !c[d.type] }))}
                        className="relative w-11 h-6 rounded-full transition-base shrink-0 mt-0.5"
                        style={{ background: on ? '#14B8A6' : 'rgba(255,255,255,0.2)' }}
                        aria-pressed={on}
                      >
                        <span className="absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-base" style={{ left: on ? 22 : 2 }} />
                      </button>
                    </div>
                  );
                })}
              </div>
              <p className="text-white/40 text-[11px] mt-4 flex items-center gap-1.5"><i className="fa-solid fa-lock" /> Handled in line with UK GDPR. Your choices are recorded.</p>
            </div>
          )}

          <div className="flex items-center justify-between mt-8">
            <button
              onClick={() => (step === 1 ? navigate('/login') : setStep((s) => s - 1))}
              className="text-white/60 hover:text-white text-sm flex items-center gap-2"
            >
              <i className="fa-solid fa-arrow-left" /> Back
            </button>
            {step < 4 ? (
              <button
                data-testid="onboarding-next"
                onClick={next}
                className="aura-btn-gradient text-white font-semibold rounded-2xl px-6 py-3 flex items-center gap-2"
              >
                Continue <i className="fa-solid fa-arrow-right" />
              </button>
            ) : (
              <button
                data-testid="onboarding-finish"
                onClick={finish}
                disabled={saving}
                className="aura-btn-gradient text-white font-semibold rounded-2xl px-6 py-3 flex items-center gap-2 disabled:opacity-70"
              >
                {saving ? <i className="fa-solid fa-circle-notch fa-spin" /> : <>Enter Auralis <i className="fa-solid fa-heart" /></>}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
